import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
    BarChartOutlined,
    CalendarOutlined,
    CarryOutOutlined,
    UserOutlined,
    CarOutlined,
    HomeOutlined,
    EnvironmentOutlined,
    GlobalOutlined,
    InboxOutlined,
    DownloadOutlined
} from '@ant-design/icons';
import MainLayout from '../../../layouts/MainLayout';
import { hasResource } from '../../../utils/resourceAuth';
import { ResourceCode } from '../../../constants/resourceCodes';
import Loading from "../../../components/Loading";
import { ErrorInfo } from "../../../components/ErrorInfo";
import { NoPermission } from "../../../components/NoPermission";
import WarehouseSelector, { WarehouseDisplayMode } from '../../../components/WarehouseSelector';
import { WarehouseProvider } from '../../../contexts/WarehouseContext';
import { useTranslation } from 'react-i18next';
import { findFirstAvailableMenuItem } from '../../../utils/menuUtils';
import { useCurrentUserOperableHubs } from "../../../hooks/useCurrentUserOperableWarehouses";
import { GET_WAREHOUSE_BY_ID, GetWarehouseByIdResponse } from '../../../graphql/queries/warehouseQueries';

const PickupLayout: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {t} = useTranslation();

    // 检测是否为地图页面
    const isMapPage = location.pathname === '/pickup/map';

    // 获取hub数据
    const { warehouses: hubs, loading, error } = useCurrentUserOperableHubs({
        fetchPolicy: 'network-only'
    });

    const [currentHub, setCurrentHub] = useState<any>(null);
    const [selectedHubId, setSelectedHubId] = useState<string>('');

    // 处理hub选择变化
    const handleHubChange = useCallback((hubId: string, siteType: number) => {
        console.log('📦 PickupLayout Hub选择:', { hubId, siteType });
        setSelectedHubId(hubId);

        const selectedHub = hubs.find(h => h.id?.toString() === hubId);
        if (selectedHub) {
            setCurrentHub(selectedHub);
        }
    }, [hubs]);

    // 自动选择第一个hub
    useEffect(() => {
        if (hubs.length > 0 && !currentHub) {
            const firstHub = hubs[0];
            console.log('📦 自动选择第一个Hub:', { id: firstHub.id, siteType: firstHub.siteType });
            setCurrentHub(firstHub);
            setSelectedHubId(firstHub.id?.toString() || '');
        }
    }, [hubs, currentHub]);

    // useCurrentUserOperableHubs 的 hub 列表字段精简（不含 timezone/appointmentDeadlineTime/pickupDeadlineTime），
    // 这里统一按当前 hub id 补查一次完整字段再合并进 WarehouseContext，全模块消费方不用各自补查
    const { data: currentHubDetailData, loading: currentHubDetailLoading } = useQuery<GetWarehouseByIdResponse>(GET_WAREHOUSE_BY_ID, {
        variables: { id: currentHub?.id ? Number(currentHub.id) : undefined },
        skip: !currentHub?.id,
    });
    const enrichedCurrentHub = useMemo(
        () => (currentHub ? { ...currentHub, ...currentHubDetailData?.warehouse, id: currentHub.id } : currentHub),
        [currentHub, currentHubDetailData]
    );
    // Outlet 要等这次补查完成再渲染，保证子组件挂载时拿到的 currentWarehouse 从一开始就是完整、稳定的对象，
    // 不会在挂载后再补一次字段导致对象引用变化（曾导致依赖 warehouse 整个引用的下游逻辑被意外触发）
    const isHubReady = !!currentHub && !currentHubDetailLoading;

    const menuItems = [
        {
            key: 'monitor',
            label: t('pickup.monitor.title'),
            icon: <BarChartOutlined />,
            display: hasResource([ResourceCode.menu.PICKUP_PROGRESS, ResourceCode.menu.PICKUP_MONITOR, ResourceCode.menu.PICKUP_DRIVER_TRACKING]),
            children: [
                {
                    key: 'progress',
                    label: t('pickup.progress.title'),
                    display: hasResource([ResourceCode.menu.PICKUP_PROGRESS]),
                    onClick: () => navigate('/pickup/progress')
                },
                {
                    key: 'driver-tracking',
                    label: t('pickup.driverTracking.menuTitle'),
                    display: hasResource([ResourceCode.menu.PICKUP_DRIVER_TRACKING]),
                    onClick: () => navigate('/pickup/driver-tracking')
                }
            ]
        },
        {
            key: 'cooperation-warehouse',
            label: t('pickup.cooperationWarehouse.title'),
            icon: <HomeOutlined />,
            // 揽收站点 2.0：审核池已作为页内页签并入本菜单（灰度 hub 才显示页签，见 PickupSitePage）
            display: hasResource([ResourceCode.menu.PICKUP_SITE]),
            onClick: () => navigate('/pickup/cooperation-warehouse')
        },
        {
            key: 'appointment',
            label: t('pickup.appointment.title'),
            icon: <CalendarOutlined />,
            display: hasResource([ResourceCode.menu.PICKUP_APPOINTMENT]),
            onClick: () => navigate('/pickup/appointment')
        },
        {
            key: 'pickup-task',
            label: t('pickup.pickupTask.title'),
            icon: <CarryOutOutlined />,
            display: hasResource([ResourceCode.menu.PICKUP_TASK]),
            onClick: () => navigate('/pickup/pickup-task')
        },
        {
            key: 'account',
            label: t('pickup.account.title'),
            icon: <UserOutlined />,
            display: hasResource([ResourceCode.menu.PICKUP_DRIVER]),
            onClick: () => navigate('/pickup/account')
        },
        {
            key: 'vehicle',
            label: t('pickup.vehicle.title'),
            icon: <CarOutlined />,
            display: hasResource([ResourceCode.menu.PICKUP_VEHICLE]),
            onClick: () => navigate('/pickup/vehicle')
        },
        {
            key: 'rally-points',
            label: t('pickup.rallyPoints.title'),
            icon: <EnvironmentOutlined />,
            display: false,
            onClick: () => navigate('/pickup/rally-points')
        },
        {
            key: 'map',
            label: t('pickup.appointment.buttons.mapRouting'),
            icon: <GlobalOutlined />,
            display: false,
            onClick: () => navigate('/pickup/map')
        },
        {
            key: 'parcels',
            label: t('pickup.parcels.title'),
            icon: <InboxOutlined />,
            display: hasResource([ResourceCode.menu.PICKUP_PACKAGE]),
            onClick: () => navigate('/pickup/parcels')
        },
        {
            key: 'download',
            label: t('pickup.download.title'),
            icon: <DownloadOutlined />,
            display: hasResource([ResourceCode.menu.PICKUP_DOWNLOAD]),
            onClick: () => navigate('/pickup/download')
        }
    ];

    useEffect(() => {
        const isRoot = location.pathname === '/pickup' || location.pathname === '/pickup/';
        if (!isRoot) return;
        const first = findFirstAvailableMenuItem(menuItems);
        if (first?.onClick) first.onClick();
    }, [location.pathname, menuItems]);

    // 二级菜单一个可点击项都没有（资源码不够）时，视为无权限，兜底展示 NoPermission 而不是空白页
    const hasVisibleMenu = useMemo(
        () => findFirstAvailableMenuItem(menuItems) !== null,
        [menuItems]
    );

    // Header布局，包含Hub选择器
    const hubSelector = (
        <div className="flex items-center gap-4 px-4 py-2">
            <WarehouseSelector
                onWarehouseChange={handleHubChange}
                displayMode={WarehouseDisplayMode.HUB_ONLY}
                warehouses={hubs}
                selectedWarehouseId={selectedHubId}
            />
        </div>
    );

    // 如果是地图页面，直接渲染内容，不使用 MainLayout
    if (isMapPage) {
        return (
            <WarehouseProvider currentWarehouse={enrichedCurrentHub}>
                <div className="h-screen w-screen">
                    {/* 只有在仓库数据加载完成 且 currentHub 补查完整字段完成后才渲染子组件 */}
                    {loading ? <Loading/> : (
                        error ? <ErrorInfo error={error}/> : (
                            (!hasVisibleMenu || hubs.length === 0) ? <NoPermission/> : (
                                isHubReady ? <Outlet/> : <Loading/>
                            )
                        )
                    )}
                </div>
            </WarehouseProvider>
        );
    }

    return (
        <WarehouseProvider currentWarehouse={enrichedCurrentHub}>
            <MainLayout
                activeFirstMenu="pickup"
                secondMenu={menuItems}
                secondHeader={hubSelector}
            >
                <div className="p-6 h-full flex flex-col">
                    {/* 只有在仓库数据加载完成 且 currentHub 补查完整字段完成后才渲染子组件 */}
                    {loading ? <Loading/> : (
                        error ? <ErrorInfo error={error}/> : (
                            (!hasVisibleMenu || hubs.length === 0) ? <NoPermission/> : (
                                isHubReady ? <Outlet/> : <Loading/>
                            )
                        )
                    )}
                </div>
            </MainLayout>
        </WarehouseProvider>
    );
};

export default PickupLayout; 
